import { connect } from 'react-redux';
import { get } from 'lodash';
import i18next from 'i18next';
import React, { useState, useEffect, useMemo } from 'react';

import { openGenericModal, refreshGenericModal } from '@actions/modal';
import { showConfirmationMessage } from '@actions/messageconfirmation';

import { Button } from '@commons/utils/styledLibraryComponents';
import { ExportButton } from '@commons/ExportButton';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getLinearGradientButtonForProgressStyling } from '@commons/utils/cssTricks';
import {
  getUserTimezone,
  formatDateWithLanguageCode,
  formatTimeWithLanguageCode,
} from '@commons/utils/date';
import LastAction from '@commons/LastAction';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';
import Text, { ENUM_FONTS } from '@commons/Text';
import WhiteCard from '@commons/WhiteCard';

import { getCentralKitchenStores } from '@selectors/stores';
import { getClientInfo } from '@selectors/client';
import theme from '@theme';

import { getRecipesOfClient } from '@services/recipe';
import backofficeService from '@services/backoffice/general';
import centralService from '@services/central';
import storeService from '@services/store';

import { SYNC_STATE } from '../commons/syncStatus';

import { Container, LastRelaunchLogContainer, TitleRow } from './styledComponents';
import { exportAnalyticsHistory } from './utils/export';
import { getRelaunchAnalyticsModalParams, ANALYTICS_TYPE } from './utils/modals';
import mixPanelUtils, { ENUM_EVENTS } from '@commons/utils/mixpanel';

const DATE_FORMAT = 'YYYY-MM-DD';
const TIMEOUT_GET_SYNC_STATUS = 5000; // in ms

const IN_PROGRESS = 'inProgress';
const INFO = 'info';
const INITIAL_STATE = {
  [IN_PROGRESS]: false,
  [INFO]: {},
};

export const BackOfficeAnalytics = (props) => {
  const {
    client: { clientId, storeName },
    match,
    user,
    stores,
  } = props;

  const userLanguageCode = get(user, 'lnkLanguageAccountrel.code', 'fr');
  const { id: userId, email: userEmail } = user;

  const [selectedStores, setSelectedStores] = useState([]);
  const [useCurrentComposition, setUseCurrentComposition] = useState(false);
  const [clientRecipes, setClientRecipes] = useState([]);
  const [selectedRecipes, setSelectedRecipes] = useState([]);

  const [dateRange, setDateRange] = useState({});

  const [analytics, setAnalytics] = useState({
    [ANALYTICS_TYPE.ORDER]: INITIAL_STATE,
    [ANALYTICS_TYPE.PRODUCTION]: INITIAL_STATE,
  });

  const [lastRelaunchLog, setLastRelaunchLog] = useState(null);

  const hasCentralKitchen = useMemo(() => getCentralKitchenStores(stores).length > 0, [stores]);

  /**
   * Handle the fetch of stores associated to the given client id
   *
   * @returns {Promise<Store[]>} The list of stores associated to the client
   */
  const retrieveAvailableStoresAndRecipes = async () => {
    try {
      const [stores, recipes, centralRecipes] = await Promise.all([
        storeService.getStoresOfClient(),
        getRecipesOfClient(),
        // Route is guarded by onlyCentralKitchen: calling it without a central kitchen store returns a 403
        hasCentralKitchen ? centralService.getKitchenRecipesOfClient(clientId) : [],
      ]);

      const clientActiveRecipes = [...recipes, ...centralRecipes].filter(({ active }) => active);

      setClientRecipes(clientActiveRecipes);

      return { stores, clientRecipes: clientActiveRecipes };
    } catch (err) {
      return [];
    }
  };

  /**
   * Handle the opening of the selection modal to relaunch analytics for a set of stores on a specific range of time
   *
   * @param {enum<RELAUNCH_ANALYTICS_TYPE>} type - The type of analytics to relaunch
   *
   * @returns {void}
   */
  const openRelaunchAnalyticsModal = async (type) => {
    const { stores, clientRecipes } = await retrieveAvailableStoresAndRecipes();

    const translatedStoreName = getClientStoreNameTranslation(storeName, true);

    const params = getRelaunchAnalyticsModalParams({
      type,
      stores,
      clientRecipes,
      selectedRecipes,
      useCurrentComposition,
      setUseCurrentComposition,
      setSelectedRecipes,
      setDateRange,
      dateRange: {},
      setSelectedStores,
      selectedStores: [],
      storeName: translatedStoreName,
      handleSave: triggerRelaunchAnalytics,
    });

    props.openGenericModal(params);

    if (type === ANALYTICS_TYPE.ORDER) {
      mixPanelUtils.sendMetric(ENUM_EVENTS.RELAUNCH_ANALYTICS, {
        userId,
        userEmail,
        clientId,
      });
    }
  };

  const getSuccessMessageForType = (type) => {
    const SUCCESS_MESSAGES = {
      [ANALYTICS_TYPE.ORDER]: i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_ORDER_SUCCESS'),
      [ANALYTICS_TYPE.PRODUCTION]: i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_PRODUCTION_SUCCESS'),
    };

    return SUCCESS_MESSAGES[type];
  };

  const getErrorMessageForType = (type) => {
    const ERROR_MESSAGES = {
      [ANALYTICS_TYPE.ORDER]: i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_ORDER_FAILURE'),
      [ANALYTICS_TYPE.PRODUCTION]: i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_PRODUCTION_FAILURE'),
    };

    return ERROR_MESSAGES[type];
  };

  /**
   * Handle the call to API to trigger the relaunch of analytics for the selected information
   *
   * @param {enum<RELAUNCH_ANALYTICS_TYPE>} type - The type of analytics to relaunch
   *
   * @returns {Promise<void>}
   */
  const triggerRelaunchAnalytics = async (type) => {
    try {
      const storeIds = selectedStores.map(({ id }) => id);

      // Send metric to Mixpanel before calling the API to relaunch analytics, as we want to track the user action immediately
      if (type === ANALYTICS_TYPE.ORDER && useCurrentComposition) {
        mixPanelUtils.sendMetric(ENUM_EVENTS.RELAUNCH_ANALYTICS_WITH_ACTUAL_RECIPE_COMPOSITION, {
          userId,
          userEmail,
          clientId,
        });
      }

      const { progress, estimatedEndDate } = await backofficeService.triggerRelaunchAnalytics({
        clientId,
        storeIds,
        type: type.toLowerCase(),
        startDate: dateRange.startDate.format(DATE_FORMAT),
        endDate: dateRange.endDate.format(DATE_FORMAT),
        userTimezone: getUserTimezone(),
        useCurrentComposition,
        recipeIds: selectedRecipes.map(({ id }) => id),
      });

      setAnalytics({
        ...analytics,
        [type]: {
          [IN_PROGRESS]: true,
          [INFO]: {
            progress,
            estimatedEndDate,
          },
        },
      });

      // Update the last relaunch log
      retrieveLastRelaunchLog();
    } catch (err) {
      props.showErrorMessage(getErrorMessageForType(type));
    }
  };

  /**
   * Handle the call to API to retrieve status of the pipeline taking care of relaunching analytics
   * for the given clientId and type
   *
   * @param {uuid} clientId                       - The id of the client on which check the status of the pipeline
   * @param {enum<RELAUNCH_ANALYTICS_TYPE>} type  - The type of analytics to relaunch
   *
   * @returns {Promise<void>}
   */
  const checkRelaunchAnalyticsStatusByType = async (clientId, type) => {
    try {
      const { state, progress, estimatedEndDate } =
        await backofficeService.getRelaunchAnalyticsStatus(clientId, type.toLowerCase());

      if ([SYNC_STATE.RETRY, SYNC_STATE.PENDING, SYNC_STATE.STARTED].includes(state)) {
        setAnalytics({
          ...analytics,
          [type]: {
            [IN_PROGRESS]: true,
            [INFO]: {
              progress,
              estimatedEndDate,
            },
          },
        });

        return;
      }

      const oneAnalyticInProgress =
        analytics[ANALYTICS_TYPE.PRODUCTION][IN_PROGRESS] ||
        analytics[ANALYTICS_TYPE.ORDER][IN_PROGRESS];

      if (oneAnalyticInProgress && state === SYNC_STATE.SUCCESS) {
        props.showSuccessMessage(getSuccessMessageForType(type));
      }

      if (state === SYNC_STATE.FAILURE || state === SYNC_STATE.REVOKED) {
        props.showErrorMessage(getErrorMessageForType(type));
      }
    } catch (error) {
      props.showErrorMessage(getErrorMessageForType(type));
    }

    setAnalytics({
      ...analytics,
      [type]: INITIAL_STATE,
    });
  };

  /**
   * Handle the call to API to retrieve the last relaunch log
   *
   * @returns {Promise<void>}
   */
  const retrieveLastRelaunchLog = async () => {
    try {
      const lastRelaunchLog = await backofficeService.getLastRelaunchLog();

      if (!lastRelaunchLog) {
        setLastRelaunchLog(null);
        return;
      }

      const formattedLastRelaunchLog = `${lastRelaunchLog?.firstName} ${
        lastRelaunchLog?.lastName
      } ${i18next.t('GENERAL.DATE_ON_AT', {
        date: lastRelaunchLog.lastRelaunchDate
          ? formatDateWithLanguageCode(lastRelaunchLog.lastRelaunchDate, userLanguageCode)
          : '',
        time: lastRelaunchLog.lastRelaunchTime
          ? formatTimeWithLanguageCode(lastRelaunchLog.lastRelaunchTime, userLanguageCode, true)
          : '',
      })}`;

      setLastRelaunchLog(formattedLastRelaunchLog);
    } catch (error) {
      props.showErrorMessage(i18next.t('BACKOFFICE.ANALYTICS.RETRIEVE_LAST_RELAUNCH_LOG_ERROR'));
      setLastRelaunchLog(null);
    }
  };

  const handleExport = async () => {
    try {
      const analyticsHistoryData = await backofficeService.getAnalyticsHistory();

      exportAnalyticsHistory(analyticsHistoryData, userLanguageCode, stores);
    } catch (error) {
      props.showErrorMessage(i18next.t('BACKOFFICE.ANALYTICS.EXPORT_ANALYTICS_HISTORY_ERROR'));
    }
  };

  // Keep track of Production Sync
  useEffect(() => {
    let intervalSync;

    if (analytics[ANALYTICS_TYPE.PRODUCTION][IN_PROGRESS]) {
      intervalSync = setInterval(() => {
        checkRelaunchAnalyticsStatusByType(clientId, ANALYTICS_TYPE.PRODUCTION);
      }, TIMEOUT_GET_SYNC_STATUS);
    }

    (async function fetchSyncStatus() {
      await checkRelaunchAnalyticsStatusByType(clientId, ANALYTICS_TYPE.PRODUCTION);
    })();

    return () => clearInterval(intervalSync);
  }, [analytics[ANALYTICS_TYPE.PRODUCTION][IN_PROGRESS]]);

  // Keep track of Order Sync
  useEffect(() => {
    let intervalSync;

    if (analytics[ANALYTICS_TYPE.ORDER][IN_PROGRESS]) {
      intervalSync = setInterval(() => {
        checkRelaunchAnalyticsStatusByType(clientId, ANALYTICS_TYPE.ORDER);
      }, TIMEOUT_GET_SYNC_STATUS);
    }

    (async function fetchSyncStatusAndLastRelaunchLog() {
      await retrieveLastRelaunchLog();
      await checkRelaunchAnalyticsStatusByType(clientId, ANALYTICS_TYPE.ORDER);
    })();

    return () => clearInterval(intervalSync);
  }, [analytics[ANALYTICS_TYPE.ORDER][IN_PROGRESS]]);

  // Handle the refresh of the selection modal
  useEffect(() => {
    const isModalOpened = get(props, 'modal.GenericModalBool', false);

    if (!isModalOpened) {
      return;
    }

    const params = getRelaunchAnalyticsModalParams({
      dateRange,
      setDateRange,
      selectedStores,
      setSelectedStores,
      clientRecipes,
      useCurrentComposition,
      setUseCurrentComposition,
      selectedRecipes,
      setSelectedRecipes,
      // To avoid keeping this information in a state
      handleSave: triggerRelaunchAnalytics,
      // To avoid keeping this information in a state
      type: get(props.modal, 'params.data.type'),
      stores: get(props.modal, 'params.data.stores', []),
      storeName: get(props.modal, 'params.data.storeName', ''),
    });

    props.refreshGenericModal(params);
  }, [dateRange, selectedStores, useCurrentComposition, selectedRecipes]);

  return (
    <Container>
      <NavigationBreadCrumb featurePath={match.path} />
      <WhiteCard
        buttonFlexColumn={false}
        buttonFlexDirection="row"
        content={i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_PRODUCTION_SUBTITLE')}
        contentColor={theme.colors.greys.darker}
        renderContent={
          <Button
            animation={{ rotate: analytics[ANALYTICS_TYPE.PRODUCTION][IN_PROGRESS] }}
            buttonCustomStyle={{ width: 'fit-content' }}
            color={'inpulse-default'}
            customBackground={getLinearGradientButtonForProgressStyling(
              analytics[ANALYTICS_TYPE.PRODUCTION][INFO].progress,
            )}
            handleClick={() => openRelaunchAnalyticsModal(ANALYTICS_TYPE.PRODUCTION)}
            icon={'/images/inpulse/sync-white.svg'}
            isDisabled={analytics[ANALYTICS_TYPE.PRODUCTION][IN_PROGRESS]}
            label={i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_ACTION')}
          />
        }
        renderLastAction={
          !!analytics[ANALYTICS_TYPE.PRODUCTION][INFO].estimatedEndDate && (
            <LastAction
              date={analytics[ANALYTICS_TYPE.PRODUCTION][INFO].estimatedEndDate}
              displayCountDown={!!analytics[ANALYTICS_TYPE.PRODUCTION][INFO].estimatedEndDate}
              label={i18next.t('GENERAL.TIME_LEFT')}
            />
          )
        }
        title={i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_PRODUCTION_TITLE')}
      />
      <WhiteCard
        buttonFlexColumn={false}
        buttonFlexDirection="row"
        content={i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_ORDER_SUBTITLE')}
        contentColor={theme.colors.greys.darker}
        renderContent={
          <>
            <Button
              animation={{ rotate: analytics[ANALYTICS_TYPE.ORDER][IN_PROGRESS] }}
              buttonCustomStyle={{ width: 'fit-content' }}
              color={'inpulse-default'}
              customBackground={getLinearGradientButtonForProgressStyling(
                analytics[ANALYTICS_TYPE.ORDER][INFO].progress,
              )}
              handleClick={() => openRelaunchAnalyticsModal(ANALYTICS_TYPE.ORDER)}
              icon={'/images/inpulse/sync-white.svg'}
              isDisabled={analytics[ANALYTICS_TYPE.ORDER][IN_PROGRESS]}
              label={i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_ACTION')}
            />
            <LastRelaunchLogContainer>
              <Text font={ENUM_FONTS.TEXT_BIG_BOLD_HEIGHT_14}>
                {i18next.t('BACKOFFICE.ANALYTICS.LAST_RELAUNCH_LOG_LABEL')}
              </Text>
              <Text>{lastRelaunchLog || '-'}</Text>
            </LastRelaunchLogContainer>
          </>
        }
        renderLastAction={
          !!analytics[ANALYTICS_TYPE.ORDER][INFO].estimatedEndDate && (
            <LastAction
              date={analytics[ANALYTICS_TYPE.ORDER][INFO].estimatedEndDate}
              displayCountDown={!!analytics[ANALYTICS_TYPE.ORDER][INFO].estimatedEndDate}
              label={i18next.t('GENERAL.TIME_LEFT')}
            />
          )
        }
        title={
          <TitleRow>
            <Text font={ENUM_FONTS.H2_INTER}>
              {i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_ORDER_TITLE')}
            </Text>
            <ExportButton
              customLabel={i18next.t('BACKOFFICE.ANALYTICS.DOWNLOAD_HISTORY')}
              customStyle={{ border: 'none', padding: 'inherit', height: 'auto' }}
              handleClick={handleExport}
            />
          </TitleRow>
        }
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  modal: state.modalReducer,
  client: getClientInfo(state.baseReducer.user),
  stores: state.baseReducer.stores,
  user: state.baseReducer.user,
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showConfirmationMessage(message, 'error'));
  },
  showSuccessMessage: (message) => {
    dispatch(showConfirmationMessage(message, 'success'));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(BackOfficeAnalytics);
